import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import PropTypes from 'prop-types';
import { generatePath, useNavigate } from 'react-router-dom';

import propOrDash from 'App/utils/propOrDash';
import PdxPatientViewModal from './PdxPatientViewModal';
import { slsSyncPdxPatient } from 'App/services/PdxPatientService';
import { findDuplicates, redirectionData, foundDuplicate, getPDXPatient, formatZipCode } from './FindPatientPdx.utils';
import useFeatureFlag from 'App/hooks/useFeatureFlag';
import { PERMISSION_TYPES } from 'App/utils/isPermitted/isPermitted';
import PdxPatientResolveDuplicateModal from './PdxPatientResolveDuplicateModal';
import { addPatientRoute, patientRoute } from 'App/common/routeLookup';
import PatientActionsCell from './PatientActionsCell';
import { PdxPatientType } from './types/PatientType';
import Table from 'App/components/Table';
import ConfirmMigrationModal from './ConfirmMigrationModal';

const propTypes = {
    data: PropTypes.arrayOf(PdxPatientType),
};

const defaultProps = {
    data: [],
};

function FindPatientPdxTable({ data }) {
    const { isSubmitting } = useFormikContext();
    const [selectedPatient, setSelectedPatient] = useState(null);
    const isSlsEnabled = useFeatureFlag(PERMISSION_TYPES.ENABLE_PDX_SLS);
    const [duplicatePatients, setDuplicatePatients] = useState();
    const [showDuplicateModal, setShowDuplicate] = useState(false);
    const [migratePatient, setMigratePatient] = useState(null);
    const navigate = useNavigate();
    const onClose = () => {
        setSelectedPatient(null);
    };
    const findPatient = (id) => {
        (async () => {
            const [patient] = await getPDXPatient(id, isSlsEnabled);
            setSelectedPatient(patient);
        })();
    };

    const showDuplicate = (patients) => {
        setDuplicatePatients(patients);
        setShowDuplicate(true);
    };

    const onDuplicateModalClose = () => {
        setDuplicatePatients(null);
        setShowDuplicate(false);
    };

    const onMigrateClick = (patientExternalId) => {
        setMigratePatient(patientExternalId);
    };

    const migrateModalClose = () => {
        setMigratePatient(null);
    };

    const migratePatientHandler = async () => {
        if (!migratePatient) return;
        try {
            if (isSlsEnabled) {
                const result = await slsSyncPdxPatient(migratePatient);
                if (result.type === 'queue' && result.matchedPatients?.length) {
                    const duplicatePatientData = await foundDuplicate(result.matchedPatients, result.response);
                    showDuplicate(duplicatePatientData);
                } else {
                    navigate(generatePath(patientRoute, { patientId: result.ID }));
                }
            } else {
                const [result] = await getPDXPatient(migratePatient, isSlsEnabled);
                const duplicateResult = await findDuplicates(result);
                if (duplicateResult) {
                    showDuplicate(duplicateResult);
                } else {
                    const patientData = redirectionData(result);
                    const query = new URLSearchParams({
                        patient: JSON.stringify(patientData),
                    }).toString();

                    // Push route with query params
                    navigate(`${generatePath(addPatientRoute)}?${query}`);
                }
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.error(err);
        } finally {
            migrateModalClose();
        }
    };

    const fixedColumns = [
        {
            Header: 'Rx.com ID',
            accessor: (row) => row?.Pdx?.rxcom_id || row?.ExternalId,
            id: 'patientId',
            canSort: true,
        },
        {
            Header: 'Last Name',
            accessor: propOrDash('LastName'),
            id: 'lastName',
            canSort: true,
        },
        {
            Header: 'First Name',
            accessor: propOrDash('FirstName'),
            id: 'firstName',
            canSort: true,
        },
        {
            Header: 'Sex',
            accessor: propOrDash('Gender'),
            id: 'gender',
            canSort: true,
        },
        {
            Header: 'Address',
            accessor: (row) => `${propOrDash('AddressLine1')(row)} ${row?.AddressLine2 ?? ''}`,
            id: 'address',
            canSort: true,
        },
        {
            Header: 'Birth Date',
            accessor: propOrDash('BirthDate'),
            id: 'birthDate',
            canSort: true,
        },
        {
            Header: 'City, State',
            accessor: (row) => `${row.City}, ${row.State}`,
            id: 'city',
            canSort: true,
        },

        {
            Header: 'Zip',
            accessor: (row) => formatZipCode(row.Zip),
            id: 'Zip',
            canSort: true,
        },
        {
            Header: 'Actions',
            id: 'actions',
            canSort: false,
            style: {
                width: 120,
            },
            Cell: PatientActionsCell(findPatient, onMigrateClick),
        },
    ];

    return (
        <>
            <Table
                data={data}
                noRowsText="Search results based on the search criteria above will display here."
                columns={fixedColumns}
                initialState={{
                    sortBy: [
                        {
                            id: 'lastName',
                            desc: false,
                        },
                    ],
                    pageSize: 10,
                }}
                pageSizeOptions={[10, 25, 50]}
                isLoading={isSubmitting}
            />
            {selectedPatient && <PdxPatientViewModal patient={selectedPatient} onClose={onClose} show />}
            {showDuplicateModal && (
                <PdxPatientResolveDuplicateModal patients={duplicatePatients} onClose={onDuplicateModalClose} />
            )}
            {migratePatient && (
                <ConfirmMigrationModal migratePatientHandler={migratePatientHandler} onClose={migrateModalClose} />
            )}
        </>
    );
}

FindPatientPdxTable.propTypes = propTypes;
FindPatientPdxTable.defaultProps = defaultProps;

export default FindPatientPdxTable;
